import * as React from 'react';

import { ModalContext } from '@notino/react-styleguide';

import { useDrawer } from '@components/BottomSheet/DrawerProvider';
import { useTrackingContext } from '@context/tracking/TrackingContext';
import { opensLinkInNewTab } from '@utils/dom';

import {
  ReviewSubmitVariant,
  trackMyReviewsClick,
  trackReviewableProductSelect,
  trackReviewSubmitClose,
  trackReviewSubmitView,
  trackWriteReviewClick,
} from './tracking';
import { ReviewableProductWithUrl } from './useReviewableProducts';

type ReviewableProductSelection = {
  product: ReviewableProductWithUrl['product'];
  /** 1-based position within the offered products. */
  position: number;
};

interface UseReviewSubmitTrackingParameters {
  /**
   * `null` until the reviewable products are resolved – the variant is decided
   * by what the screen ends up showing, so the view event has to wait for it.
   */
  variant: ReviewSubmitVariant | null;
}

export const useReviewSubmitTracking = ({
  variant,
}: UseReviewSubmitTrackingParameters) => {
  const { getTimeFromInit } = useTrackingContext();
  const { isShown: isModalShown } = ModalContext.useModalContext();
  const { isDrawerOpen } = useDrawer();

  const viewTimeRef = React.useRef<number | null>(null);
  const isClosedTrackedRef = React.useRef(false);

  const isVisible = isModalShown || isDrawerOpen;

  React.useEffect(() => {
    // The drawer keeps its content mounted after closing (and remounts it once
    // to reset the closing animation), so the visibility check also guards
    // against a phantom view of an already closed screen.
    if (!variant || !isVisible || viewTimeRef.current !== null) {
      return;
    }

    viewTimeRef.current = new Date().getTime();
    trackReviewSubmitView({ variant, timing: getTimeFromInit() });
  }, [variant, isVisible, getTimeFromInit]);

  const trackClose = React.useCallback(() => {
    if (viewTimeRef.current === null || isClosedTrackedRef.current) {
      return;
    }

    isClosedTrackedRef.current = true;
    trackReviewSubmitClose();
  }, []);

  React.useEffect(() => {
    if (!isVisible) {
      trackClose();
    }
  }, [isVisible, trackClose]);

  // Covers the screen being replaced without the modal/drawer closing – e.g.
  // retrying a failed image upload brings the progress screen back.
  React.useEffect(() => () => trackClose(), [trackClose]);

  // Time since the view event, not since the last page_view.
  const getTimeFromView = () =>
    getTimeFromInit(viewTimeRef.current ?? undefined);

  // Unless it opens in a new tab, the subpage is left behind by both links, so
  // its data is cleared right after the click event is sent.
  return {
    trackMyReviews: (event: React.SyntheticEvent) => {
      trackMyReviewsClick({ timing: getTimeFromView() });

      if (!opensLinkInNewTab(event)) {
        trackClose();
      }
    },
    // "Write a review" leads to the product detail as well, so the item select
    // is reported first and the click on top of it.
    trackWriteReview: (
      event: React.SyntheticEvent,
      { product, position }: ReviewableProductSelection
    ) => {
      trackReviewableProductSelect({ product, position });
      trackWriteReviewClick({ timing: getTimeFromView() });

      if (!opensLinkInNewTab(event)) {
        trackClose();
      }
    },
    trackProductSelect: (
      event: React.SyntheticEvent,
      { product, position }: ReviewableProductSelection
    ) => {
      trackReviewableProductSelect({ product, position });

      if (!opensLinkInNewTab(event)) {
        trackClose();
      }
    },
  };
};
